import React, { FC, useState, useRef, useEffect, useId } from 'react';
import { matchPrefersReducedMotion } from 'style/styled-components/cssMediaQueries';
import { ModalDialog } from 'components/ModalDialog/ModalDialog';
import SVGInline from 'components/SVGInline/SVGInline';
import { useKeyboardShortcuts } from 'hooks/useKeyboardShortcuts/useKeyboardShortcuts';
import { useBackdropClose } from 'hooks/useBackdropClose/useBackdropClose';
import { withNoScroll } from 'hooks/withNoScroll/withNoScroll';
import icons from 'style';
import translate from 'utils/fnTranslate';
import { CardService } from 'components/Cards/CardSubscription';
import { ServiceOptionCover } from 'types/Cover';
import { ModalNavigation, ModalCloseButton, ModalWrapper } from 'style/styled-components/reusable.css';
import {
    CardWrapperScrollable,
    OptionsContent,
    OptionSubtext,
    OptionTitle,
    ScrollWrapper,
    ScrollButton,
    CardServiceWrapper,
} from './UpsellOptionSelector.css';

type PropTypes = {
    options: ServiceOptionCover[];
    onClose: () => void;
    onBack: () => void;
};

const ServiceSelector: FC<PropTypes> = ({ options, onClose, onBack }) => {
    const titleId = useId();
    const descriptionId = useId();
    const backdropClose = useBackdropClose(onClose);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [isDesktop, setIsDesktop] = useState(false);

    useKeyboardShortcuts({
        backspace: onBack,
    });

    useEffect(() => {
        const checkDesktop = () => {
            setIsDesktop(window.innerWidth >= 768);
        };

        checkDesktop();
        window.addEventListener('resize', checkDesktop);
        return () => window.removeEventListener('resize', checkDesktop);
    }, []);

    const checkScroll = () => {
        if (scrollContainerRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 5);
        }
    };

    useEffect(() => {
        checkScroll();
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScroll);
            window.addEventListener('resize', checkScroll);
            return () => {
                container.removeEventListener('scroll', checkScroll);
                window.removeEventListener('resize', checkScroll);
            };
        }
        return undefined;
    }, [options]);

    const scroll = (direction: 'left' | 'right') => {
        if (scrollContainerRef.current) {
            const scrollAmount = 250;
            const newPosition =
                direction === 'left'
                    ? scrollContainerRef.current.scrollLeft - scrollAmount
                    : scrollContainerRef.current.scrollLeft + scrollAmount;

            scrollContainerRef.current.scrollTo({
                left: newPosition,
                behavior: matchPrefersReducedMotion() ? 'auto' : 'smooth',
            });
        }
    };

    const shouldShowScrollButtons = isDesktop && options.length > 7;

    return (
        <ModalDialog
            as={ModalWrapper}
            onClose={onClose}
            initialFocusRef={scrollContainerRef}
            opacity={1}
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            {...backdropClose}
        >
            <ModalNavigation {...backdropClose}>
                <ModalCloseButton type="button" onClick={onClose} tabIndex={0} aria-label={translate('CLOSE_BUTTON')}>
                    <SVGInline svg={icons.closeIcon} />
                </ModalCloseButton>
            </ModalNavigation>
            <OptionsContent {...backdropClose}>
                <OptionTitle id={titleId}>{`${translate('SCREEN_UPSELL_TITLE')}`}</OptionTitle>
                <OptionSubtext id={descriptionId}>
                    {translate('SCREEN_UPSELL_SUBTEXT')} <br />
                    {translate('SCREEN_UPSELL_SUBTEXT_SECONDARY')}
                </OptionSubtext>

                <ScrollWrapper {...backdropClose}>
                    {shouldShowScrollButtons && canScrollLeft && (
                        <ScrollButton position="left" onClick={() => scroll('left')} aria-label={translate('ARIA_HINT_GALLERY_LEFT')}>
                            <SVGInline svg={icons.icoGalleryLeft} />
                        </ScrollButton>
                    )}
                    <CardWrapperScrollable ref={scrollContainerRef} $isScrollable={shouldShowScrollButtons} {...backdropClose}>
                        {options.map((option, index) => (
                            <CardServiceWrapper key={`${index}-${option.id}`} $isScrollable={shouldShowScrollButtons}>
                                <CardService asset={option} />
                            </CardServiceWrapper>
                        ))}
                    </CardWrapperScrollable>
                    {shouldShowScrollButtons && canScrollRight && (
                        <ScrollButton position="right" onClick={() => scroll('right')} aria-label={translate('ARIA_HINT_GALLERY_RIGHT')}>
                            <SVGInline svg={icons.icoGalleryRight} />
                        </ScrollButton>
                    )}
                </ScrollWrapper>
            </OptionsContent>
        </ModalDialog>
    );
};

export const UpsellOptionSelector = withNoScroll(ServiceSelector);
